import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectSocket, disconnectSocket } from '../services/socket';
import {
  notificationReceived,
  socketConnectionChanged,
} from '../features/notifications/notificationsSlice';
import { getMode } from '../utils/functions';

/**
 * Holds the app's socket open for as long as there is a session.
 *
 * Mounted once, in `AppShell` — the frame that exists exactly when a user is
 * authenticated, whatever the profile. Keeping the connection above the pages is
 * the whole point: in the legacy the listener lives inside the page that started
 * the export, so walking away from it drops the notification the user was
 * waiting for.
 *
 * The role sent on `open` is `getMode(user)`, not the legacy's
 * `user_type[0]`. Three of the four backend producers publish with the role
 * hardcoded to "admin", but they back admin-only actions (status history,
 * sellers export, the dashboard refresh). The deliveries export, the one a
 * seller also has, publishes with the user's own first permission, which is
 * what `getMode` returns for a single-profile account. They only diverge for a
 * user who holds several profiles — noted as a backend fix.
 */
export const useNotificationsSocket = () => {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const userId = user?.id;
  const role = getMode(user);

  useEffect(() => {
    if (!isAuthenticated || !userId || !role) return undefined;

    connectSocket({
      userId,
      role,
      onEvent: (channel, payload) =>
        dispatch(notificationReceived({ channel, payload, role, userId })),
      onStatusChange: (isConnected) => dispatch(socketConnectionChanged(isConnected)),
    });

    return () => disconnectSocket();
  }, [dispatch, isAuthenticated, userId, role]);
};
